import React, { useState, useEffect  } from 'react';
import { motion } from 'framer-motion';
import {
    Phone,
    Mail,
    MapPin,
    Clock,
    Send,
    MessageCircle,
    Star,
    Award,
    Shield,
    Truck,
    HeadphonesIcon
} from 'lucide-react';

const Contacto = () => {


  // 👇 Scroll siempre al inicio cuando se carga el componente
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);


    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const contactInfo = [
        {
            icon: Phone,
            title: 'Teléfono',
            content: '+51 955 000 321',
            link: 'tel:+51955000321',
            description: 'Llámanos para consultas inmediatas',
            gradient: 'from-blue-500 to-cyan-500'
        },
        {
            icon: Mail,
            title: 'Email',
            content: 'info@v7shop.com',
            link: 'mailto:info@v7shop.com',
            description: 'Escríbenos para cualquier consulta',
            gradient: 'from-purple-500 to-pink-500'
        },
        {
            icon: MapPin,
            title: 'Dirección',
            content: 'Av. Principal 123, Lima 15001',
            link: 'https://maps.google.com/?q=Av.+Principal+123,+Lima+15001',
            description: 'Visita nuestra tienda física',
            gradient: 'from-green-500 to-emerald-500'
        },
        {
            icon: Clock,
            title: 'Horario de Atención',
            content: 'Lun - Vie: 9:00 AM - 7:00 PM',
            description: 'Sábados: 9:00 AM - 2:00 PM',
            gradient: 'from-orange-500 to-red-500'
        }
    ];

    const features = [
        { icon: Truck, title: 'Envío Gratis', description: 'En compras mayores a S/ 200' },
        { icon: Shield, title: 'Garantía', description: '30 días de garantía en todos los productos' },
        { icon: HeadphonesIcon, title: 'Soporte 24/7', description: 'Atención personalizada siempre' },
        { icon: Award, title: 'Calidad Premium', description: 'Productos de la más alta calidad' }
    ];

    const stats = [
        { number: '10K+', label: 'Clientes Satisfechos' },
        { number: '5+', label: 'Años de Experiencia' },
        { number: '99%', label: 'Rating Positivo' },
        { number: '24/7', label: 'Soporte' }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
            <div className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Hero */}
                    <motion.div
                        className="text-center mb-20"
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div
                            className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-6 py-3 border border-blue-100 mb-8"
                            whileHover={{ scale: 1.05 }}
                        >
                            <Star className="w-4 h-4 text-blue-500" />
                            <span className="text-gray-700 text-sm font-medium">Contáctanos</span>
                        </motion.div>

                        <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
                            Estamos Aquí para Ayudarte
                        </h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            Nuestro equipo está listo para responder tus dudas y brindarte la mejor atención.
                            Escríbenos o visítanos en nuestras oficinas.
                        </p>
                    </motion.div>

                    {/* Stats */}
                    <motion.div
                        className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="text-center p-6 bg-white rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300"
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-gray-600 font-medium">{stat.label}</div>
                            </motion.div>
                        ))}
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
                        {/* Contact Info */}
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="space-y-6"
                        >
                            <motion.h2
                                className="text-3xl font-bold text-gray-900 mb-8"
                                variants={itemVariants}
                            >
                                Información de Contacto
                            </motion.h2>

                            {contactInfo.map((item, index) => (
                                <motion.div
                                    key={index}
                                    variants={itemVariants}
                                    whileHover={{ y: -5 }}
                                    className="bg-white rounded-2xl p-6 border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300"
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className={`w-14 h-14 bg-gradient-to-r ${item.gradient} rounded-xl flex items-center justify-center`}>
                                            <item.icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-1">{item.title}</h3>
                                            {item.link ? (
                                                <a
                                                    href={item.link}
                                                    target={item.link.includes('http') ? '_blank' : '_self'}
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline font-medium block mb-1"
                                                >
                                                    {item.content}
                                                </a>
                                            ) : (
                                                <p className="text-gray-800 font-medium mb-1">{item.content}</p>
                                            )}
                                            <p className="text-gray-500 text-sm">{item.description}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Mapa + Formulario */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="space-y-8"
                        >
                            {/* Mapa */}
                            <motion.div
                                whileHover={{ y: -3 }}
                                className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden"
                            >
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3901.965646244739!2d-77.0427850247761!3d-12.046959288187797!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9105c5f619ee3ec7%3A0x14206cb9cc452b4a!2sLima%2015001!5e0!3m2!1ses!2spe!4v1699999999999!5m2!1ses!2spe"
                                    width="100%"
                                    height="300"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    title="Ubicación de V7 Shop en Lima"
                                    className="w-full"
                                />
                                <div className="p-6">
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Nuestra Ubicación</h3>
                                    <p className="text-gray-600">
                                        Visítanos en nuestro local principal en Lima. Fácil acceso y estacionamiento disponible.
                                    </p>
                                </div>
                            </motion.div>

                            {/* Formulario */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="bg-white rounded-2xl p-8 border border-gray-100 shadow-md"
                            >
                                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Envíanos un Mensaje</h3>
                                <p className="text-gray-500 mb-6">Te responderemos en menos de 24 horas</p>

                                <form className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Tu nombre completo"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="+51 XXX XXX XXX"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="tu@email.com"
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        placeholder="Asunto"
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <textarea
                                        name="message"
                                        rows="4"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Escribe tu mensaje..."
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                    />
                                    <motion.button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-4 px-6 rounded-xl font-semibold flex items-center justify-center space-x-2 shadow hover:shadow-lg transition-all"
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <Send className="w-5 h-5" />
                                        <span>Enviar Mensaje</span>
                                    </motion.button>
                                </form>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* Features */}
                    <motion.div
                        className="mb-20"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                    >
                        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                            ¿Por qué elegir <span className="text-blue-600">V7 Shop</span>?
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {features.map((feature, index) => (
                                <motion.div
                                    key={index}
                                    className="bg-white rounded-2xl p-6 text-center border border-gray-100 shadow-md hover:shadow-lg transition-all"
                                    whileHover={{ y: -5, scale: 1.02 }}
                                >
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <feature.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                                    <p className="text-gray-600">{feature.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* WhatsApp flotante mejorado */}
            <motion.a
                href="https://wa.me/51955000321?text=¡Hola!%20Estoy%20interesado%20en%20sus%20productos%20y%20servicios.%20¿Podrían%20brindarme%20más%20información?"
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-8 right-8 flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg hover:shadow-emerald-500/40 z-50 group"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
                {/* Ícono de WhatsApp */}
                <MessageCircle className="w-8 h-8 text-white group-hover:scale-110 transition-transform duration-300" />

                {/* Tooltip */}
                <span className="absolute right-20 bg-gray-900 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Chatea con nosotros
                </span>

                {/* Efecto de pulso */}
                <span className="absolute inset-0 rounded-full bg-green-500 opacity-30 animate-ping"></span>
            </motion.a>

        </div>
    );
};

export default Contacto;
